import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { clientDocumentAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import './ClientDocumentFolder.css';

/**
 * Recursively render folder tree with expand/collapse
 */
function FolderTree({ folders, selectedFolder, onSelectFolder, fileCounts, expandedFolders, onToggleExpand, level = 0 }) {
  return (
    <ul className={`folder-tree ${level === 0 ? 'root' : ''}`}>
      {folders.map((folder) => {
        const folderKey = (folder.key && String(folder.key).trim()) || slugFromName(folder.name);
        const count = fileCounts[folder.key] ?? fileCounts[folderKey] ?? 0;
        const isSelected = selectedFolder === folder.key || selectedFolder === folderKey;
        const hasChildren = folder.children && folder.children.length > 0;
        const isExpanded = expandedFolders[folder.key] || false;
        
        return (
          <li key={folder.key} className="folder-item">
            <div className={`folder-row ${isSelected ? 'selected' : ''}`}>
              {/* Expand/collapse arrow */}
              {hasChildren && (
                <span 
                  className="folder-arrow"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand(folder.key);
                  }}
                >
                  {isExpanded ? '▼' : '▶'}
                </span>
              )}
              {!hasChildren && <span className="folder-arrow-spacer"></span>}
              
              {/* Folder content */}
              <div 
                className="folder-content"
                onClick={() => onSelectFolder(folderKey)}
              >
                <span className="folder-icon">{hasChildren ? '📁' : '📂'}</span>
                <span className="folder-name">{folder.name}</span>
                {count > 0 && <span className="folder-count">({count})</span>}
              </div>
            </div>
            
            {/* Children (only show if expanded) */}
            {hasChildren && isExpanded && (
              <FolderTree
                folders={folder.children}
                selectedFolder={selectedFolder}
                onSelectFolder={onSelectFolder}
                fileCounts={fileCounts}
                expandedFolders={expandedFolders}
                onToggleExpand={onToggleExpand}
                level={level + 1}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Upload dialog with "Save As" + optional target folder (defaults to folder active when upload started).
 * onConfirm(saveAsNameWithExtension, targetFolderKey)
 */
function UploadDialog({
  file,
  folderOptions,
  initialTargetFolderKey,
  getSuggestionsForFolder,
  onConfirm,
  onCancel,
}) {
  const [saveAsName, setSaveAsName] = useState('');
  const [targetFolderKey, setTargetFolderKey] = useState('');

  // Initialize with original filename (without extension for easier editing)
  useEffect(() => {
    if (file) {
      const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
      setSaveAsName(nameWithoutExt);
    }
  }, [file]);

  useEffect(() => {
    const key = initialTargetFolderKey && String(initialTargetFolderKey).trim();
    if (key && folderOptions?.some((o) => o.key === key)) {
      setTargetFolderKey(key);
    } else if (folderOptions?.length) {
      setTargetFolderKey(folderOptions[0].key);
    } else {
      setTargetFolderKey('');
    }
  }, [file, initialTargetFolderKey, folderOptions]);

  if (!file) return null;

  const suggestions = getSuggestionsForFolder ? getSuggestionsForFolder(targetFolderKey) : [];
  const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
  
  // Clean suggestion for use as filename (remove ~ markers)
  const cleanSuggestion = (suggestion) => {
    // Remove leading/trailing ~ and clean up
    let clean = suggestion.replace(/^~+|~+$/g, '').trim();
    // Remove extension from suggestion if it has one (we'll add the real one)
    const suggestionExt = clean.substring(clean.lastIndexOf('.'));
    if (suggestionExt.toLowerCase() === '.pdf' || suggestionExt.toLowerCase() === '.docx' || suggestionExt.toLowerCase() === '.doc') {
      clean = clean.substring(0, clean.lastIndexOf('.'));
    }
    return clean;
  };

  const handleSuggestionClick = (suggestion) => {
    setSaveAsName(cleanSuggestion(suggestion));
  };

  const handleConfirm = () => {
    if (!targetFolderKey) return;
    onConfirm(saveAsName.trim() + fileExt, targetFolderKey);
  };

  return (
    <div className="upload-dialog-overlay" onClick={onCancel}>
      <div className="upload-dialog" onClick={e => e.stopPropagation()}>
        <h3>Save File As</h3>
        
        <div className="upload-dialog-field">
          <label>Original file:</label>
          <span className="original-filename">{file.name}</span>
        </div>

        <div className="upload-dialog-field">
          <label htmlFor="uploadTargetFolder">Upload to folder</label>
          <select
            id="uploadTargetFolder"
            value={targetFolderKey}
            onChange={(e) => setTargetFolderKey(e.target.value)}
            disabled={!folderOptions?.length}
          >
            {(folderOptions || []).map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
          <p className="upload-dialog-suggestions-hint" style={{ marginTop: '0.35rem' }}>
            Defaults to the folder you had open; change this to file elsewhere without switching the tree.
          </p>
        </div>
        
        <div className="upload-dialog-field">
          <label htmlFor="saveAsInput">Save as:</label>
          <div className="save-as-input-group">
            <input
              id="saveAsInput"
              type="text"
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              autoFocus
            />
            <span className="file-extension">{fileExt}</span>
          </div>
        </div>
        
        <div className="upload-dialog-suggestions">
          <label>Suggested names (for the upload folder above):</label>
          {suggestions && suggestions.length > 0 ? (
            <div className="suggestion-buttons">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className="suggestion-btn"
                  onClick={() => handleSuggestionClick(s)}
                  title={s}
                >
                  {cleanSuggestion(s)}
                </button>
              ))}
            </div>
          ) : (
            <p className="upload-dialog-suggestions-hint">
              No suggested names for the selected upload folder. Add them in <strong>Settings → Client Folder Template</strong> for that folder.
            </p>
          )}
        </div>

        <div className="upload-dialog-actions">
          <button className="btn-cancel" onClick={onCancel}>Cancel</button>
          <button 
            className="btn-confirm" 
            onClick={handleConfirm}
            disabled={!saveAsName.trim() || !targetFolderKey}
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
}

/** Slug from folder name for matching when key is missing or empty (must match ClientFolderTemplate logic). */
function slugFromName(name) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    || 'folder';
}

/** Natural sort (numeric collation, like Windows Explorer): "2 …" before "10 …", not lexicographic 1, 10, 2. */
function compareDisplayName(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base', numeric: true });
}

/** Normalize folder_key (e.g. "4 Correspondence/2 Post-IFSP") to template key style (e.g. "4-correspondence/2-post-ifsp") for nested folder matching. */
function folderKeyToTemplateKey(folderKey) {
  if (!folderKey || typeof folderKey !== 'string') return '';
  return folderKey.split('/').map((s) => slugFromName(s.trim())).filter(Boolean).join('/');
}

/** Flatten folder tree to [{ key, label }] for dropdowns. Nested folders use "Parent / Child" label. */
function flattenFoldersForDropdown(folders, prefix = '') {
  if (!folders || !Array.isArray(folders)) return [];
  const out = [];
  for (const folder of folders) {
    const key = (folder.key && String(folder.key).trim()) || slugFromName(folder.name);
    const label = prefix ? `${prefix} / ${folder.name}` : folder.name;
    out.push({ key, label });
    if (folder.children && folder.children.length > 0) {
      out.push(...flattenFoldersForDropdown(folder.children, prefix ? `${prefix} / ${folder.name}` : folder.name));
    }
  }
  return out;
}

/** Whether a file belongs to the folder currently selected in the tree (same rules as the main file list). */
function fileBelongsToSelectedFolder(file, selectedFolder) {
  if (!selectedFolder || !file?.folder_key) return false;
  return (
    file.folder_key === selectedFolder ||
    slugFromName(file.folder_key) === selectedFolder ||
    folderKeyToTemplateKey(file.folder_key) === selectedFolder
  );
}

/** True if a stored folder_key is the same template folder as tree key (for move destinations). */
function folderKeyMatchesTreeKey(storedFolderKey, treeFolderKey) {
  if (!storedFolderKey || !treeFolderKey) return false;
  return fileBelongsToSelectedFolder({ folder_key: storedFolderKey }, treeFolderKey);
}

/** Folders every file in `files` can move to together (intersection of per-file valid destinations). */
function commonMoveDestinationOptions(files, folders) {
  const all = flattenFoldersForDropdown(folders);
  if (!files?.length || !all.length) return [];
  const keySets = files.map((f) =>
    new Set(all.filter((o) => !folderKeyMatchesTreeKey(f.folder_key, o.key)).map((o) => o.key))
  );
  let keys = [...keySets[0]];
  for (let i = 1; i < keySets.length; i += 1) {
    keys = keys.filter((k) => keySets[i].has(k));
  }
  return all.filter((o) => keys.includes(o.key));
}

/** Human-readable folder label for a stored folder_key using rows from flattenFoldersForDropdown. */
function resolveFolderLabelForFile(folderKey, folderRows) {
  if (!folderKey) return 'Unknown folder';
  const tk = folderKeyToTemplateKey(folderKey);
  const slug = slugFromName(folderKey);
  for (const { key, label } of folderRows) {
    if (folderKey === key || tk === key || slug === key || slugFromName(folderKey) === key) {
      return label;
    }
  }
  return folderKey;
}

/**
 * PDFs for insert source: current folder first (by filename), then other folders (by folder_key, then filename).
 * Each entry has display text "Folder label / filename".
 */
function buildInsertPdfSourceOptions(allFiles, targetFile, selectedFolder, folderRows) {
  const isPdf = (f) =>
    (f?.content_type || '').toLowerCase() === 'application/pdf' || /\.pdf$/i.test(f?.original_file_name || '');
  const candidates = (allFiles || []).filter((f) => f.id !== targetFile?.id && isPdf(f));
  const cmpName = (a, b) => compareDisplayName(a.original_file_name, b.original_file_name);

  const inCurrent = selectedFolder
    ? candidates.filter((f) => fileBelongsToSelectedFolder(f, selectedFolder))
    : [];
  const inCurrentSorted = [...inCurrent].sort(cmpName);
  const rest = selectedFolder
    ? candidates.filter((f) => !fileBelongsToSelectedFolder(f, selectedFolder))
    : candidates;

  const byFolder = new Map();
  for (const f of rest) {
    const k = f.folder_key || '';
    if (!byFolder.has(k)) byFolder.set(k, []);
    byFolder.get(k).push(f);
  }
  const folderKeysSorted = [...byFolder.keys()].sort((a, b) => {
    const ca = folderKeyToTemplateKey(a) || a;
    const cb = folderKeyToTemplateKey(b) || b;
    return compareDisplayName(ca, cb) || compareDisplayName(a, b);
  });

  const out = [];
  for (const f of inCurrentSorted) {
    const fl = resolveFolderLabelForFile(f.folder_key, folderRows);
    out.push({ file: f, optionLabel: `${fl} / ${f.original_file_name}` });
  }
  for (const fk of folderKeysSorted) {
    const group = byFolder.get(fk).sort(cmpName);
    const fl = resolveFolderLabelForFile(fk, folderRows);
    for (const f of group) {
      out.push({ file: f, optionLabel: `${fl} / ${f.original_file_name}` });
    }
  }
  return out;
}

const BULK_UPLOAD_MAX_FILES = 30;

/**
 * Format file size for display
 */
function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isPdfFile(file) {
  if (!file) return false;
  const ct = (file.content_type || '').toLowerCase();
  return ct === 'application/pdf' || /\.pdf$/i.test(file.original_file_name || '');
}

const FILE_ACTIONS_MENU_WIDTH = 220;
const FILE_ACTIONS_MENU_EST_HEIGHT = 300;

/**
 * Portaled overflow menu so it is not clipped by the scrollable files list.
 * fileMenu: { file, rect: DOMRect } | null
 */
function ClientFolderFileActionsMenuPortal({
  fileMenu,
  onClose,
  canUpload,
  canDelete,
  onPreviewPdf,
  onDownload,
  onRename,
  onMove,
  onVersions,
  onInsert,
  onDelete,
}) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!fileMenu?.rect) return;
    const { rect } = fileMenu;
    let left = rect.right - FILE_ACTIONS_MENU_WIDTH;
    if (left < 8) left = 8;
    if (left + FILE_ACTIONS_MENU_WIDTH > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - FILE_ACTIONS_MENU_WIDTH - 8);
    }
    let top = rect.bottom + 6;
    if (top + FILE_ACTIONS_MENU_EST_HEIGHT > window.innerHeight - 8 && rect.top > FILE_ACTIONS_MENU_EST_HEIGHT + 12) {
      top = Math.max(8, rect.top - FILE_ACTIONS_MENU_EST_HEIGHT - 6);
    }
    setPosition({ top, left });
  }, [fileMenu]);

  if (!fileMenu?.file) return null;

  const file = fileMenu.file;
  const pdf = isPdfFile(file);
  const ver = file.current_version_number != null ? file.current_version_number : 1;

  const run = (fn) => (e) => {
    e.stopPropagation();
    onClose();
    fn(file);
  };

  return createPortal(
    <>
      <div className="file-actions-menu-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        role="menu"
        className="file-actions-menu-dropdown-portal"
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: FILE_ACTIONS_MENU_WIDTH,
          zIndex: 1060,
        }}
      >
        {pdf && (
          <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(onPreviewPdf)}>
            Preview PDF…
          </button>
        )}
        <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(onDownload)}>
          Download
        </button>
        <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(onRename)}>
          Rename…
        </button>
        {canUpload && (
          <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(onMove)}>
            Move to folder…
          </button>
        )}
        <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(onVersions)}>
          Version history (v{ver})
        </button>
        {canUpload && pdf && (
          <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(onInsert)}>
            Insert pages…
          </button>
        )}
        {canDelete && (
          <>
            <div className="file-actions-menu-separator" role="separator" />
            <button
              type="button"
              role="menuitem"
              className="file-actions-menu-item file-actions-menu-item-danger"
              onClick={run(onDelete)}
            >
              Delete…
            </button>
          </>
        )}
      </div>
    </>,
    document.body
  );
}

/**
 * Rename dialog for editing existing file names
 */
function RenameDialog({ file, suggestions, onConfirm, onCancel }) {
  const [newFileName, setNewFileName] = useState('');
  
  // Initialize with current filename (without extension for easier editing)
  useEffect(() => {
    if (file) {
      const nameWithoutExt = file.original_file_name.substring(0, file.original_file_name.lastIndexOf('.')) || file.original_file_name;
      setNewFileName(nameWithoutExt);
    }
  }, [file]);

  if (!file) return null;

  const fileExt = file.original_file_name.substring(file.original_file_name.lastIndexOf('.')).toLowerCase();
  
  // Clean suggestion for use as filename (remove ~ markers)
  const cleanSuggestion = (suggestion) => {
    let clean = suggestion.replace(/^~+|~+$/g, '').trim();
    const suggestionExt = clean.substring(clean.lastIndexOf('.'));
    if (suggestionExt.toLowerCase() === '.pdf' || suggestionExt.toLowerCase() === '.docx' || suggestionExt.toLowerCase() === '.doc') {
      clean = clean.substring(0, clean.lastIndexOf('.'));
    }
    return clean;
  };

  const handleSuggestionClick = (suggestion) => {
    setNewFileName(cleanSuggestion(suggestion));
  };

  const handleConfirm = () => {
    onConfirm(newFileName.trim() + fileExt);
  };

  return (
    <div className="upload-dialog-overlay" onClick={onCancel}>
      <div className="upload-dialog" onClick={e => e.stopPropagation()}>
        <h3>Rename File</h3>
        
        <div className="upload-dialog-field">
          <label>Current name:</label>
          <span className="original-filename">{file.original_file_name}</span>
        </div>
        
        <div className="upload-dialog-field">
          <label htmlFor="renameInput">New name:</label>
          <div className="save-as-input-group">
            <input
              id="renameInput"
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              autoFocus
            />
            <span className="file-extension">{fileExt}</span>
          </div>
        </div>

        <div className="upload-dialog-suggestions">
          <label>Suggested names (from template):</label>
          {suggestions && suggestions.length > 0 ? (
            <div className="suggestion-buttons">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className="suggestion-btn"
                  onClick={() => handleSuggestionClick(s)}
                  title={s}
                >
                  {cleanSuggestion(s)}
                </button>
              ))}
            </div>
          ) : (
            <p className="upload-dialog-suggestions-hint">
              No suggested names for this folder. Add them in <strong>Settings → Client Folder Template</strong>.
            </p>
          )}
        </div>

        <div className="upload-dialog-actions">
          <button className="btn-cancel" onClick={onCancel}>Cancel</button>
          <button 
            className="btn-confirm" 
            onClick={handleConfirm}
            disabled={!newFileName.trim()}
          >
            Rename
          </button>
        </div>
      </div>
    </div>
  );
}

function MoveFileDialog({ file, fromLabel, destinationOptions, onConfirm, onCancel, submitting }) {
  const [targetKey, setTargetKey] = useState('');

  useEffect(() => {
    if (destinationOptions?.length) {
      setTargetKey(destinationOptions[0].key);
    } else {
      setTargetKey('');
    }
  }, [file, destinationOptions]);

  if (!file) return null;

  return (
    <div className="upload-dialog-overlay" onClick={onCancel}>
      <div className="upload-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Move file</h3>

        <div className="upload-dialog-field">
          <label>File</label>
          <span className="original-filename">{file.original_file_name}</span>
        </div>

        <div className="upload-dialog-field">
          <label>Current folder</label>
          <span className="original-filename">{fromLabel}</span>
        </div>

        <div className="upload-dialog-field">
          <label htmlFor="moveTargetFolder">Move to</label>
          <select
            id="moveTargetFolder"
            value={targetKey}
            onChange={(e) => setTargetKey(e.target.value)}
            disabled={!destinationOptions?.length || submitting}
          >
            {destinationOptions.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>

        <p className="upload-dialog-suggestions-hint" style={{ marginTop: '0.5rem' }}>
          Only the folder assignment is updated. The file is not re-uploaded.
        </p>

        <div className="upload-dialog-actions">
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button
            type="button"
            className="btn-confirm"
            onClick={() => onConfirm(targetKey)}
            disabled={submitting || !targetKey || !destinationOptions?.length}
          >
            {submitting ? 'Moving…' : 'Move'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkDeleteConfirmDialog({ files, onConfirm, onCancel, submitting, error }) {
  if (!files?.length) return null;
  return (
    <div className="upload-dialog-overlay" onClick={onCancel}>
      <div className="upload-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Delete {files.length} file{files.length === 1 ? '' : 's'}?</h3>
        <div className="upload-dialog-field">
          <label>Files to be deleted</label>
          <ul className="bulk-move-files-dialog-list">
            {files.map((f) => (
              <li key={f.id}>{f.original_file_name}</li>
            ))}
          </ul>
        </div>
        <p className="bulk-delete-warning">This cannot be undone. The selected files will be permanently removed.</p>
        {error && <p className="bulk-delete-error">{error}</p>}
        <div className="upload-dialog-actions">
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button
            type="button"
            className="btn-bulk-delete-confirm"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? 'Deleting…' : `Delete ${files.length} file${files.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkMoveFilesDialog({ files, fromLabel, destinationOptions, onConfirm, onCancel, submitting }) {
  const [targetKey, setTargetKey] = useState('');

  useEffect(() => {
    if (destinationOptions?.length) {
      setTargetKey(destinationOptions[0].key);
    } else {
      setTargetKey('');
    }
  }, [files, destinationOptions]);

  if (!files?.length) return null;

  return (
    <div className="upload-dialog-overlay" onClick={onCancel}>
      <div className="upload-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Move {files.length} file{files.length === 1 ? '' : 's'}</h3>

        <div className="upload-dialog-field">
          <label>Files</label>
          <ul className="bulk-move-files-dialog-list">
            {files.map((f) => (
              <li key={f.id}>{f.original_file_name}</li>
            ))}
          </ul>
        </div>

        <div className="upload-dialog-field">
          <label>Current folder</label>
          <span className="original-filename">{fromLabel}</span>
        </div>

        <div className="upload-dialog-field">
          <label htmlFor="bulkMoveTargetFolder">Move all to</label>
          <select
            id="bulkMoveTargetFolder"
            value={targetKey}
            onChange={(e) => setTargetKey(e.target.value)}
            disabled={!destinationOptions?.length || submitting}
          >
            {destinationOptions.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>

        <p className="upload-dialog-suggestions-hint" style={{ marginTop: '0.5rem' }}>
          All selected files are moved to the same folder. Names must stay unique in the destination (rename first if needed).
        </p>

        <div className="upload-dialog-actions">
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button
            type="button"
            className="btn-confirm"
            onClick={() => onConfirm(targetKey)}
            disabled={submitting || !targetKey || !destinationOptions?.length}
          >
            {submitting ? 'Moving…' : `Move ${files.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function InsertPagesDialog({
  targetFile,
  allFiles,
  selectedFolder,
  folderRows,
  onConfirm,
  onCancel,
  onPreviewTarget,
  submitting,
}) {
  const [sourceMode, setSourceMode] = useState('existing');
  const [sourceFileId, setSourceFileId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [insertPlacement, setInsertPlacement] = useState('end');
  const [afterPageNumber, setAfterPageNumber] = useState('');

  const sourceOptions = useMemo(
    () => buildInsertPdfSourceOptions(allFiles, targetFile, selectedFolder, folderRows || []),
    [allFiles, targetFile?.id, selectedFolder, folderRows]
  );

  useEffect(() => {
    if (sourceOptions.length > 0) setSourceFileId(String(sourceOptions[0].file.id));
    else setSourceFileId('');
  }, [targetFile?.id, selectedFolder, sourceOptions]);

  const canSubmit = sourceMode === 'existing'
    ? !!sourceFileId
    : !!uploadFile;

  const submit = () => {
    const normalizedAfter = parseInt(afterPageNumber || '0', 10);
    const safeAfter = Number.isNaN(normalizedAfter) || normalizedAfter < 0 ? 0 : normalizedAfter;
    const payload = {
      insertMode: insertPlacement === 'after_page' ? 'page_index' : insertPlacement,
      // Backend page_index inserts before index. "After page N" maps to page_index=N.
      pageIndex: insertPlacement === 'after_page' ? safeAfter : null,
      sourceFileId: sourceMode === 'existing' ? parseInt(sourceFileId, 10) : null,
      sourceFile: sourceMode === 'upload' ? uploadFile : null,
    };
    onConfirm(payload);
  };

  return (
    <div className="upload-dialog-overlay" onClick={onCancel}>
      <div className="upload-dialog insert-pages-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Insert Pages Into "{targetFile?.original_file_name}"</h3>
        <p className="insert-pages-helper">
          Source can be a client-folder PDF file or a new PDF upload. To pick a page position,
          preview the current PDF in a new tab.
        </p>
        <div className="insert-pages-preview-row">
          <button
            type="button"
            className="btn-download-zip"
            onClick={onPreviewTarget}
            disabled={submitting}
          >
            Preview Current PDF
          </button>
        </div>

        <div className="upload-dialog-field">
          <label>Source type</label>
          <select value={sourceMode} onChange={(e) => setSourceMode(e.target.value)} disabled={submitting}>
            <option value="existing">Existing PDF from this client (folders below)</option>
            <option value="upload">Upload PDF</option>
          </select>
        </div>

        {sourceMode === 'existing' ? (
          <div className="upload-dialog-field">
            <label>Choose source file</label>
            <select value={sourceFileId} onChange={(e) => setSourceFileId(e.target.value)} disabled={submitting}>
              {sourceOptions.map(({ file: f, optionLabel }) => (
                <option key={f.id} value={f.id}>
                  {optionLabel}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="upload-dialog-field">
            <label>Upload source PDF</label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              disabled={submitting}
            />
          </div>
        )}

        <div className="upload-dialog-field">
          <label>Insert location</label>
          <select value={insertPlacement} onChange={(e) => setInsertPlacement(e.target.value)} disabled={submitting}>
            <option value="start">Beginning of document</option>
            <option value="end">End of document</option>
            <option value="after_page">After page number…</option>
          </select>
        </div>

        {insertPlacement === 'after_page' && (
          <div className="upload-dialog-field">
            <label>Insert after page # (1-based)</label>
            <input
              type="number"
              min="1"
              value={afterPageNumber}
              onChange={(e) => setAfterPageNumber(e.target.value)}
              disabled={submitting}
            />
          </div>
        )}

        <div className="upload-dialog-actions">
          <button className="btn-cancel" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button className="btn-confirm" onClick={submit} disabled={!canSubmit || submitting}>
            {submitting ? 'Saving…' : 'Insert Pages'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VersionHistoryDialog({ file, versions, onDownloadVersion, onClose, loading }) {
  return (
    <div className="upload-dialog-overlay" onClick={onClose}>
      <div className="upload-dialog version-history-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Version History: {file?.original_file_name}</h3>
        {loading ? (
          <p>Loading versions…</p>
        ) : (
          <div className="version-history-list">
            {versions.length === 0 ? (
              <p>No saved versions yet.</p>
            ) : (
              versions.map((v) => (
                <div className="version-history-item" key={v.id}>
                  <div>
                    <strong>v{v.version_number}</strong>{' '}
                    <span className="version-history-meta">{formatDisplayDate(v.created_at)} · {v.operation || 'upload'}</span>
                    {v.source_file_name && <span className="version-history-meta"> · source: {v.source_file_name}</span>}
                  </div>
                  <div>
                    <button className="btn-download-icon" onClick={() => onDownloadVersion(v)} title="Download this version">
                      ⬇️
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        <div className="upload-dialog-actions">
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Bulk upload modal: list of files with folder dropdown and Save as per row; Apply folder to all; Upload all.
 */
function BulkUploadModal({
  files,
  assignments,
  folderOptions,
  getSuggestionsForFolder,
  onAssignmentChange,
  onApplyFolderToAll,
  onUploadAll,
  onCancel,
  uploading,
  uploadStatus,
}) {
  const defaultFolderKey = folderOptions.length > 0 ? folderOptions[0].key : '';

  const cleanSuggestion = (suggestion) => {
    let clean = (suggestion || '').replace(/^~+|~+$/g, '').trim();
    const ext = clean.substring(clean.lastIndexOf('.'));
    if (['.pdf', '.docx', '.doc'].includes(ext.toLowerCase())) {
      clean = clean.substring(0, clean.lastIndexOf('.'));
    }
    return clean;
  };

  return (
    <div className="upload-dialog-overlay bulk-upload-modal-overlay" onClick={onCancel}>
      <div className="bulk-upload-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Bulk upload – assign folders</h3>
        <p className="bulk-upload-intro">
          Assign each file to a folder and optionally change the save name. Then click Upload all.
        </p>

        <div className="bulk-upload-apply-row">
          <label htmlFor="bulk-apply-folder">Apply folder to all:</label>
          <select
            id="bulk-apply-folder"
            value=""
            onChange={(e) => {
              const key = e.target.value;
              if (key) onApplyFolderToAll(key);
              e.target.value = '';
            }}
          >
            <option value="">Choose folder…</option>
            {folderOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
          <span className="bulk-upload-apply-hint">Select a folder and it will be applied to every row.</span>
        </div>

        <div className="bulk-upload-table-wrap">
          <table className="bulk-upload-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Folder</th>
                <th>Save as</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file, index) => {
                const a = assignments[index] || { folderKey: defaultFolderKey, saveAsName: file.name };
                const suggestions = a.folderKey ? (getSuggestionsForFolder(a.folderKey) || []) : [];
                const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
                const nameWithoutExt = file.name.includes('.') ? file.name.substring(0, file.name.lastIndexOf('.')) : file.name;
                return (
                  <tr key={index}>
                    <td className="bulk-upload-cell-filename" title={file.name}>
                      {file.name}
                    </td>
                    <td className="bulk-upload-cell-folder">
                      <select
                        value={a.folderKey || ''}
                        onChange={(e) => onAssignmentChange(index, 'folderKey', e.target.value)}
                        disabled={uploading}
                        aria-label={`Folder for ${file.name}`}
                      >
                        <option value="">Select folder</option>
                        {folderOptions.map((opt) => (
                          <option key={opt.key} value={opt.key}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="bulk-upload-cell-saveas">
                      <div className="bulk-upload-saveas-row">
                        <input
                          type="text"
                          value={a.saveAsName ?? file.name}
                          onChange={(e) => onAssignmentChange(index, 'saveAsName', e.target.value)}
                          disabled={uploading}
                          aria-label={`Save as for ${file.name}`}
                        />
                        {ext && <span className="bulk-upload-ext">{ext}</span>}
                      </div>
                      {suggestions.length > 0 && (
                        <div className="bulk-upload-suggestions">
                          {suggestions.map((s, i) => (
                            <button
                              key={i}
                              type="button"
                              className="suggestion-btn"
                              onClick={() => onAssignmentChange(index, 'saveAsName', cleanSuggestion(s) + ext)}
                              disabled={uploading}
                              title={s}
                            >
                              {cleanSuggestion(s)}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {uploadStatus && <p className="bulk-upload-status">{uploadStatus}</p>}

        <div className="upload-dialog-actions">
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={uploading}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-confirm"
            onClick={onUploadAll}
            disabled={uploading || assignments.some((a) => !a?.folderKey)}
          >
            {uploading ? 'Uploading…' : 'Upload all'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientDocumentFolder() {
  const { id } = useParams();
  const clientId = id;

  // State
  const [template, setTemplate] = useState(null);
  const [allFiles, setAllFiles] = useState(null);
  const [fileCounts, setFileCounts] = useState({});
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  
  // Upload dialog state
  const [pendingFile, setPendingFile] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Folder expand/collapse state
  const [expandedFolders, setExpandedFolders] = useState({});
  
  // Rename dialog state
  const [renameFile, setRenameFile] = useState(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);

  // Multi-select for bulk download
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Bulk upload: files chosen, per-file folder + saveAs, modal visible, upload progress
  const [bulkFiles, setBulkFiles] = useState(null);
  const [bulkAssignments, setBulkAssignments] = useState([]);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadStatus, setBulkUploadStatus] = useState('');
  const bulkFileInputRef = React.useRef(null);
  const [insertTargetFile, setInsertTargetFile] = useState(null);
  const [showInsertDialog, setShowInsertDialog] = useState(false);
  const [insertSubmitting, setInsertSubmitting] = useState(false);
  const [versionTargetFile, setVersionTargetFile] = useState(null);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [versionList, setVersionList] = useState([]);
  const [versionLoading, setVersionLoading] = useState(false);

  const [moveFile, setMoveFile] = useState(null);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveSubmitting, setMoveSubmitting] = useState(false);

  const [bulkMoveFiles, setBulkMoveFiles] = useState(null);
  const [showBulkMoveDialog, setShowBulkMoveDialog] = useState(false);
  const [bulkMoveSubmitting, setBulkMoveSubmitting] = useState(false);
  const [bulkDeleteFiles, setBulkDeleteFiles] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteSubmitting, setBulkDeleteSubmitting] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState(null);

  /** { file, rect: DOMRect } when the per-row ⋯ menu is open (portaled dropdown). */
  const [fileMenu, setFileMenu] = useState(null);

  // File list sort: 'name' | 'uploaded', 'asc' | 'desc'
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Permissions
  const canUpload = hasPermission('documents.upload');
  const canDelete = hasPermission('documents.delete');

  // Find folder info from template (match by key, or by slug of name when key is missing/empty)
  const findFolder = useCallback((folders, key) => {
    if (!key) return null;
    for (const folder of folders) {
      const keyMatch = folder.key && String(folder.key).trim() === key;
      const slugMatch = (!folder.key || !String(folder.key).trim()) && slugFromName(folder.name) === key;
      if (keyMatch || slugMatch) return folder;
      if (folder.children) {
        const found = findFolder(folder.children, key);
        if (found) return found;
      }
    }
    return null;
  }, []);
  
  // Toggle folder expand/collapse
  const handleToggleExpand = useCallback((folderKey) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderKey]: !prev[folderKey]
    }));
  }, []);
  
  // Auto-expand parent folders when a folder is selected
  const autoExpandParents = useCallback((folders, targetKey, parents = []) => {
    for (const folder of folders) {
      if (folder.key === targetKey) {
        // Found the target, expand all parents
        const newExpanded = {};
        parents.forEach(parentKey => {
          newExpanded[parentKey] = true;
        });
        return newExpanded;
      }
      if (folder.children && folder.children.length > 0) {
        const result = autoExpandParents(folder.children, targetKey, [...parents, folder.key]);
        if (result) return result;
      }
    }
    return null;
  }, []);

  const selectedFolderInfo = template?.template_structure?.folders 
    ? findFolder(template.template_structure.folders, selectedFolder)
    : null;

  const insertFolderRows = useMemo(
    () => flattenFoldersForDropdown(template?.template_structure?.folders || []),
    [template]
  );

  const moveDestinationOptions = useMemo(() => {
    if (!moveFile) return [];
    const all = flattenFoldersForDropdown(template?.template_structure?.folders || []);
    return all.filter((o) => !folderKeyMatchesTreeKey(moveFile.folder_key, o.key));
  }, [moveFile, template]);

  const selectedFilesInCurrentFolder = useMemo(
    () => folderFiles.filter((f) => selectedFileIds.includes(f.id)),
    [folderFiles, selectedFileIds]
  );

  const bulkMoveDestinationOptions = useMemo(() => {
    if (!bulkMoveFiles?.length) return [];
    return commonMoveDestinationOptions(bulkMoveFiles, template?.template_structure?.folders || []);
  }, [bulkMoveFiles, template]);

  // Standardized "Save as" names for this folder (from template); never undefined
  const folderSuggestions = (selectedFolderInfo?.fileTemplates || []).filter(
    (s) => s != null && String(s).trim() !== ''
  );

  const normalizeAndSetFolderCounts = useCallback((rawCounts) => {
    const countsWithSlugs = { ...(rawCounts || {}) };
    Object.keys(rawCounts || {}).forEach((k) => {
      countsWithSlugs[slugFromName(k)] = rawCounts[k];
      const templateKey = folderKeyToTemplateKey(k);
      if (templateKey) countsWithSlugs[templateKey] = rawCounts[k];
    });
    setFileCounts(countsWithSlugs);
  }, []);

  const refreshFolderSummary = useCallback(async () => {
    const summaryRes = await clientDocumentAPI.getClientFileSummary(clientId);
    normalizeAndSetFolderCounts(summaryRes.data?.data?.folderCounts || {});
  }, [clientId, normalizeAndSetFolderCounts]);

  const refreshSelectedFolderFiles = useCallback(async () => {
    if (!selectedFolder) {
      setFolderFiles([]);
      return;
    }
    const folderRes = await clientDocumentAPI.getFolderFiles(clientId, selectedFolder);
    setFolderFiles(Array.isArray(folderRes.data?.data) ? folderRes.data.data : []);
  }, [clientId, selectedFolder]);

  useEffect(() => {
    if (!fileMenu) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setFileMenu(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fileMenu]);

  useEffect(() => {
    if (!fileMenu) return undefined;
    const close = () => setFileMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [fileMenu]);

  const ensureAllFilesLoaded = useCallback(async () => {
    if (Array.isArray(allFiles)) return allFiles;
    const filesRes = await clientDocumentAPI.getClientFiles(clientId);
    const next = filesRes.data?.data?.files || [];
    setAllFiles(next);
    normalizeAndSetFolderCounts(filesRes.data?.data?.folderCounts || {});
    return next;
  }, [allFiles, clientId, normalizeAndSetFolderCounts]);

  // Load initial data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        // Load only lightweight data required for first paint.
        const [templateRes, summaryRes] = await Promise.all([
          clientDocumentAPI.getTemplate(),
          clientDocumentAPI.getClientFileSummary(clientId)
        ]);

        setTemplate(templateRes.data.data);
        normalizeAndSetFolderCounts(summaryRes.data?.data?.folderCounts || {});

        // Select first folder by default (use key if present and non-empty, else slug of name so findFolder can resolve it)
        if (templateRes.data.data?.template_structure?.folders?.length > 0) {
          const first = templateRes.data.data.template_structure.folders[0];
          const firstFolderKey = (first.key && String(first.key).trim()) || slugFromName(first.name);
          setSelectedFolder(firstFolderKey);
          
          // Auto-expand parents of first folder
          const parentsToExpand = autoExpandParents(
            templateRes.data.data.template_structure.folders,
            firstFolderKey
          );
          if (parentsToExpand) {
            setExpandedFolders(parentsToExpand);
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
        if (err.response?.status === 404 && err.response?.data?.error?.includes('template')) {
          setError('No document folder template configured for this tenant. Contact your administrator.');
        } else {
          setError('Failed to load client documents');
        }
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [clientId, normalizeAndSetFolderCounts]);

  // Fallback: if template has folders but selectedFolder is falsy (e.g. first folder had empty key), set it to first folder's resolvable key
  useEffect(() => {
    const folders = template?.template_structure?.folders;
    if (!folders?.length || (selectedFolder && String(selectedFolder).trim())) return;
    const first = folders[0];
    const firstKey = (first.key && String(first.key).trim()) || slugFromName(first.name) || 'folder-0';
    setSelectedFolder(firstKey);
  }, [template, selectedFolder]);

  // Load files for selected folder
  useEffect(() => {
    if (!selectedFolder) {
      setFolderFiles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await clientDocumentAPI.getFolderFiles(clientId, selectedFolder);
        if (!cancelled) setFolderFiles(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        console.error('Error loading folder files:', err);
        if (!cancelled) setFolderFiles([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedFolder, clientId]);

  useEffect(() => {
    setFileMenu(null);
  }, [selectedFolder]);

  // Handle file selection - show dialog instead of uploading directly
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedFolder) return;
    
    // Store the file and show the save-as dialog
    setPendingFile(file);
    setShowUploadDialog(true);
    
    // Reset file input so same file can be selected again
    event.target.value = '';
  };

  // Unified upload: allow selecting 1 or many files in one picker
  const handleUploadClick = () => {
    if (!canUpload || uploading) return;
    if (!selectedFolderInfo) {
      alert('Select a folder first.');
      return;
    }
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
      bulkFileInputRef.current.click();
    }
  };

  // Handle upload dialog cancel
  const handleUploadCancel = () => {
    setPendingFile(null);
    setShowUploadDialog(false);
  };

  const handleBulkFileSelect = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) return;
    // If user picked exactly one file, route to the existing single-file Save-As dialog flow.
    if (selected.length === 1) {
      const file = selected[0];
      if (!selectedFolder) {
        alert('Select a folder first.');
        event.target.value = '';
        return;
      }
      setPendingFile(file);
      setShowUploadDialog(true);
      event.target.value = '';
      return;
    }
    const capped = selected.slice(0, BULK_UPLOAD_MAX_FILES);
    if (capped.length < selected.length) {
      alert(`Maximum ${BULK_UPLOAD_MAX_FILES} files. Only the first ${BULK_UPLOAD_MAX_FILES} were added.`);
    }
    const defaultFolderKey = selectedFolder || (template?.template_structure?.folders?.length
      ? (() => {
          const first = template.template_structure.folders[0];
          return (first.key && String(first.key).trim()) || slugFromName(first.name);
        })()
      : '');
    setBulkFiles(capped);
    setBulkAssignments(capped.map((file) => ({ folderKey: defaultFolderKey, saveAsName: file.name })));
    setBulkUploadStatus('');
    setShowBulkUploadModal(true);
    event.target.value = '';
  };

  const getSuggestionsForFolder = useCallback((folderKey) => {
    if (!template?.template_structure?.folders || !folderKey) return [];
    const info = findFolder(template.template_structure.folders, folderKey);
    return (info?.fileTemplates || []).filter((s) => s != null && String(s).trim() !== '');
  }, [template, findFolder]);

  const handleBulkAssignmentChange = (index, field, value) => {
    setBulkAssignments((prev) => {
      const next = [...prev];
      next[index] = { ...(next[index] || {}), [field]: value };
      return next;
    });
  };

  const handleApplyFolderToAll = (folderKey) => {
    if (!folderKey) return;
    setBulkAssignments((prev) => prev.map((a) => ({ ...a, folderKey })));
  };

  const handleBulkUploadAll = async () => {
    if (!bulkFiles?.length || !clientId) return;
    const allHaveFolder = bulkFiles.every((_, i) => bulkAssignments[i]?.folderKey);
    if (!allHaveFolder) {
      alert('Please select a folder for every file.');
      return;
    }
    setUploading(true);
    const total = bulkFiles.length;
    try {
      for (let i = 0; i < total; i++) {
        setBulkUploadStatus(`Uploading ${i + 1} of ${total}…`);
        const file = bulkFiles[i];
        const a = bulkAssignments[i] || {};
        const saveAsName = (a.saveAsName || file.name).trim();
        await clientDocumentAPI.uploadFile(
          clientId,
          a.folderKey,
          file,
          saveAsName,
          () => {}
        );
      }
      await Promise.all([refreshFolderSummary(), refreshSelectedFolderFiles()]);
      setAllFiles(null);
      setBulkFiles(null);
      setBulkAssignments([]);
      setShowBulkUploadModal(false);
      setBulkUploadStatus('');
    } catch (err) {
      console.error('Bulk upload error:', err);
      alert(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setBulkUploadStatus('');
    }
  };

  const handleBulkUploadCancel = () => {
    setShowBulkUploadModal(false);
    setBulkFiles(null);
    setBulkAssignments([]);
    setBulkUploadStatus('');
  };

  const handleOpenInsertDialog = async (fileRecord) => {
    try {
      await ensureAllFilesLoaded();
      setInsertTargetFile(fileRecord);
      setShowInsertDialog(true);
    } catch (err) {
      console.error('Error loading insert source files:', err);
      alert('Failed to load source file list for insert pages.');
    }
  };

  const handlePreviewPdfFile = async (fileRecord) => {
    if (!fileRecord) return;
    try {
      const response = await clientDocumentAPI.downloadFile(fileRecord.id);
      const blob = new Blob([response.data], { type: fileRecord.content_type || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Error previewing file:', err);
      alert('Failed to preview current PDF.');
    }
  };

  const handleInsertConfirm = async (payload) => {
    if (!insertTargetFile) return;
    try {
      setInsertSubmitting(true);
      await clientDocumentAPI.insertIntoFile(insertTargetFile.id, payload);
      await Promise.all([refreshFolderSummary(), refreshSelectedFolderFiles()]);
      setAllFiles(null);
      setShowInsertDialog(false);
      setInsertTargetFile(null);
    } catch (err) {
      console.error('Error inserting pages:', err);
      alert(err.response?.data?.error || err.message || 'Failed to insert pages.');
    } finally {
      setInsertSubmitting(false);
    }
  };

  const handleOpenVersions = async (fileRecord) => {
    try {
      setVersionTargetFile(fileRecord);
      setShowVersionDialog(true);
      setVersionLoading(true);
      const res = await clientDocumentAPI.getFileVersions(fileRecord.id);
      setVersionList(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('Error loading versions:', err);
      alert(err.response?.data?.error || err.message || 'Failed to load version history.');
    } finally {
      setVersionLoading(false);
    }
  };

  const handleDownloadVersion = async (version) => {
    if (!versionTargetFile || !version) return;
    try {
      const response = await clientDocumentAPI.downloadFileVersion(versionTargetFile.id, version.id);
      const blob = new Blob([response.data], { type: version.content_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = version.original_file_name || versionTargetFile.original_file_name || `version-${version.version_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading version:', err);
      alert(err.response?.data?.error || err.message || 'Failed to download version.');
    }
  };

  // Handle upload dialog confirm - actually upload the file
  const handleUploadConfirm = async (saveAsName, targetFolderKey) => {
    if (!pendingFile || !targetFolderKey) return;

    try {
      setShowUploadDialog(false);
      setUploading(true);
      setUploadProgress(0);

      await clientDocumentAPI.uploadFile(
        clientId,
        targetFolderKey,
        pendingFile,
        saveAsName,
        (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      );

      // Reload files
      await Promise.all([refreshFolderSummary(), refreshSelectedFolderFiles()]);
      setAllFiles(null);

      setPendingFile(null);
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload file: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle file download
  const handleDownload = async (fileRecord) => {
    try {
      const response = await clientDocumentAPI.downloadFile(fileRecord.id);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: fileRecord.content_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileRecord.original_file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file');
    }
  };

  const handleOpenFile = async (fileRecord) => {
    if (!fileRecord) return;
    if (isPdfFile(fileRecord)) {
      await handlePreviewPdfFile(fileRecord);
    } else {
      await handleDownload(fileRecord);
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFileIds((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  const selectAllInFolder = () => {
    const ids = folderFiles.map((f) => f.id);
    const allInFolderSelected = ids.length > 0 && ids.every((id) => selectedFileIds.includes(id));
    if (allInFolderSelected) {
      setSelectedFileIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedFileIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const handleBulkDownloadZip = async (fileIdsToUse) => {
    if (!fileIdsToUse?.length) return;
    try {
      setBulkDownloading(true);
      const response = await clientDocumentAPI.bulkDownloadFiles(clientId, fileIdsToUse);
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = 'ClientFolder_download.zip';
      if (disposition) {
        const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) || disposition.match(/filename=["']?([^"';]+)["']?/i);
        if (match) filename = decodeURIComponent(match[1].trim());
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSelectedFileIds([]);
    } catch (err) {
      console.error('Error bulk downloading:', err);
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          alert(json.error || json.message || 'Failed to download ZIP');
        } catch {
          alert('Failed to download ZIP');
        }
      } else {
        alert(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to download ZIP');
      }
    } finally {
      setBulkDownloading(false);
    }
  };

  // Handle file delete
  const handleDelete = async (fileRecord) => {
    if (!window.confirm(`Delete "${fileRecord.original_file_name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await clientDocumentAPI.deleteFile(fileRecord.id);

      // Reload files
      await Promise.all([refreshFolderSummary(), refreshSelectedFolderFiles()]);
      setAllFiles(null);
    } catch (err) {
      console.error('Error deleting file:', err);
      alert('Failed to delete file');
    }
  };
  
  // Handle file rename
  const handleRename = (fileRecord) => {
    setRenameFile(fileRecord);
    setShowRenameDialog(true);
  };

  const handleOpenMove = (fileRecord) => {
    const all = flattenFoldersForDropdown(template?.template_structure?.folders || []);
    const dest = all.filter((o) => !folderKeyMatchesTreeKey(fileRecord.folder_key, o.key));
    if (dest.length === 0) {
      window.alert('There are no other folders to move this file to.');
      return;
    }
    setMoveFile(fileRecord);
    setShowMoveDialog(true);
  };

  const handleMoveCancel = () => {
    if (moveSubmitting) return;
    setShowMoveDialog(false);
    setMoveFile(null);
  };

  const handleMoveConfirm = async (targetFolderKey) => {
    if (!moveFile || !targetFolderKey) return;
    try {
      setMoveSubmitting(true);
      await clientDocumentAPI.moveFileToFolder(moveFile.id, targetFolderKey);
      await Promise.all([refreshFolderSummary(), refreshSelectedFolderFiles()]);
      setAllFiles(null);
      setShowMoveDialog(false);
      setMoveFile(null);
    } catch (err) {
      console.error('Error moving file:', err);
      window.alert(err.response?.data?.error || err.message || 'Failed to move file');
    } finally {
      setMoveSubmitting(false);
    }
  };

  const handleOpenBulkMove = () => {
    const files = selectedFilesInCurrentFolder;
    if (files.length === 0) {
      window.alert('Select one or more files in this folder using the checkboxes.');
      return;
    }
    const opts = commonMoveDestinationOptions(files, template?.template_structure?.folders || []);
    if (opts.length === 0) {
      window.alert('There are no folders all selected files can be moved to together.');
      return;
    }
    setBulkMoveFiles(files);
    setShowBulkMoveDialog(true);
  };

  const handleBulkMoveCancel = () => {
    if (bulkMoveSubmitting) return;
    setShowBulkMoveDialog(false);
    setBulkMoveFiles(null);
  };

  const handleBulkMoveConfirm = async (targetFolderKey) => {
    if (!bulkMoveFiles?.length || !targetFolderKey) return;
    try {
      setBulkMoveSubmitting(true);
      for (const f of bulkMoveFiles) {
        await clientDocumentAPI.moveFileToFolder(f.id, targetFolderKey);
      }
      await Promise.all([refreshFolderSummary(), refreshSelectedFolderFiles()]);
      setAllFiles(null);
      setSelectedFileIds([]);
      setShowBulkMoveDialog(false);
      setBulkMoveFiles(null);
    } catch (err) {
      console.error('Error moving files:', err);
      window.alert(err.response?.data?.error || err.message || 'Failed to move one or more files');
      try {
        await Promise.all([refreshFolderSummary(), refreshSelectedFolderFiles()]);
        setAllFiles(null);
      } catch (refreshErr) {
        console.error('Error refreshing after partial move:', refreshErr);
      }
      setShowBulkMoveDialog(false);
      setBulkMoveFiles(null);
      setSelectedFileIds([]);
    } finally {
      setBulkMoveSubmitting(false);
    }
  };
  
  const handleOpenBulkDelete = () => {
    const files = selectedFilesInCurrentFolder;
    if (files.length === 0) return;
    setBulkDeleteFiles(files);
    setBulkDeleteError(null);
    setShowBulkDeleteDialog(true);
  };

  const handleBulkDeleteCancel = () => {
    if (bulkDeleteSubmitting) return;
    setShowBulkDeleteDialog(false);
    setBulkDeleteFiles(null);
    setBulkDeleteError(null);
  };

  const handleBulkDeleteConfirm = async () => {
    if (!bulkDeleteFiles?.length) return;
    setBulkDeleteSubmitting(true);
    setBulkDeleteError(null);
    const results = await Promise.allSettled(
      bulkDeleteFiles.map((f) => clientDocumentAPI.deleteFile(f.id))
    );
    const failed = results.filter((r) => r.status === 'rejected');
    try {
      await Promise.all([refreshFolderSummary(), refreshSelectedFolderFiles()]);
      setAllFiles(null);
      setSelectedFileIds((prev) => {
        const deletedIds = new Set(bulkDeleteFiles.map((f) => f.id));
        return prev.filter((id) => !deletedIds.has(id));
      });
    } catch (refreshErr) {
      console.error('Error refreshing after bulk delete:', refreshErr);
    }
    setBulkDeleteSubmitting(false);
    if (failed.length > 0) {
      setBulkDeleteError(`${failed.length} file${failed.length === 1 ? '' : 's'} could not be deleted. The rest were removed.`);
    } else {
      setShowBulkDeleteDialog(false);
      setBulkDeleteFiles(null);
    }
  };

  // Confirm rename
  const handleRenameConfirm = async (newFileName) => {
    if (!newFileName || !renameFile) return;
    
    try {
      await clientDocumentAPI.renameFile(renameFile.id, newFileName);
      
      // Reload files
      await Promise.all([refreshFolderSummary(), refreshSelectedFolderFiles()]);
      setAllFiles(null);
      
      setShowRenameDialog(false);
      setRenameFile(null);
    } catch (err) {
      console.error('Error renaming file:', err);
      alert('Failed to rename file: ' + (err.response?.data?.error || err.message));
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="client-document-folder">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="client-document-folder">
        <div className="client-document-folder-header">
          <h1>Client Folder</h1>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="client-document-folder">
      <div className="client-document-folder-header">
        <h1>Client Folder</h1>
        <div className="client-document-folder-header-actions">
          {template?.template_structure?.folders?.length > 0 && (
            <button
              type="button"
              className="btn-download-zip btn-download-selected"
              onClick={() => handleBulkDownloadZip(selectedFileIds)}
              disabled={bulkDownloading || selectedFileIds.length === 0}
              title={selectedFileIds.length === 0 ? 'Select files from any folder below to download as ZIP' : `Download ${selectedFileIds.length} selected as ZIP`}
            >
              {bulkDownloading ? 'Preparing ZIP…' : `Download selected as ZIP${selectedFileIds.length > 0 ? ` (${selectedFileIds.length})` : ''}`}
            </button>
          )}
          {canUpload && selectedFilesInCurrentFolder.length > 0 && (
            <button
              type="button"
              className="btn-download-zip btn-move-selected"
              onClick={handleOpenBulkMove}
              disabled={bulkMoveSubmitting || bulkDownloading || uploading}
              title={`Move ${selectedFilesInCurrentFolder.length} selected file${selectedFilesInCurrentFolder.length === 1 ? '' : 's'} in this folder to another folder`}
            >
              Move selected… ({selectedFilesInCurrentFolder.length})
            </button>
          )}
          {canDelete && selectedFilesInCurrentFolder.length > 0 && (
            <button
              type="button"
              className="btn-download-zip btn-delete-selected"
              onClick={handleOpenBulkDelete}
              disabled={bulkDeleteSubmitting || bulkMoveSubmitting || bulkDownloading || uploading}
              title={`Delete ${selectedFilesInCurrentFolder.length} selected file${selectedFilesInCurrentFolder.length === 1 ? '' : 's'}`}
            >
              Delete selected… ({selectedFilesInCurrentFolder.length})
            </button>
          )}
          {canUpload && (
            <>
              <button
                type="button"
                className={`btn-upload ${!selectedFolderInfo ? 'btn-upload-disabled' : ''}`}
                onClick={handleUploadClick}
                disabled={uploading || !selectedFolderInfo}
                title={!selectedFolderInfo ? 'Select a folder first' : 'Upload one or more files to the selected folder'}
              >
                {uploading ? `Uploading... ${uploadProgress}%` : 'Upload…'}
              </button>
              <input
                type="file"
                ref={bulkFileInputRef}
                onChange={handleBulkFileSelect}
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
                style={{ display: 'none' }}
                aria-hidden="true"
              />
            </>
          )}
        </div>
      </div>

      <div className="client-document-folder-content">
        {/* Left panel: Folder tree */}
        <div className="folder-panel">
          <h2>Folders</h2>
          {template?.template_structure?.folders && (
            <FolderTree
              folders={template.template_structure.folders}
              selectedFolder={selectedFolder}
              onSelectFolder={setSelectedFolder}
              fileCounts={fileCounts}
              expandedFolders={expandedFolders}
              onToggleExpand={handleToggleExpand}
            />
          )}
        </div>

        {/* Right panel: Files in selected folder */}
        <div className="files-panel">
          <div className="files-header">
            <h2>{selectedFolderInfo ? selectedFolderInfo.name : 'Select a folder to view or upload files'}</h2>
          </div>

          {selectedFolderInfo ? (
            <>
              {/* Files list */}
              <div className="files-list">
                {folderFiles.length === 0 ? (
                  <div className="no-files">No files in this folder</div>
                ) : (
                  <table className="files-table">
                    <thead>
                      <tr>
                        <th className="files-table-col-select" title="Select all">
                          <input
                            type="checkbox"
                            checked={folderFiles.length > 0 && folderFiles.every((f) => selectedFileIds.includes(f.id))}
                            onChange={selectAllInFolder}
                            aria-label="Select all files in folder"
                          />
                        </th>
                        <th
                          className="files-table-col-filename sortable"
                          onClick={() => handleSort('name')}
                          title="Sort by file name"
                        >
                          File Name{' '}
                          {sortField === 'name'
                            ? (sortDir === 'asc' ? '▲' : '▼')
                            : <span className="sort-arrow-inactive">↕</span>}
                        </th>
                        <th
                          className="files-table-col-uploaded sortable"
                          onClick={() => handleSort('uploaded')}
                          title="Sort by upload date"
                        >
                          Uploaded{' '}
                          {sortField === 'uploaded'
                            ? (sortDir === 'asc' ? '▲' : '▼')
                            : <span className="sort-arrow-inactive">↕</span>}
                        </th>
                        <th className="files-table-col-actions">More</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...folderFiles]
                        .sort((a, b) => {
                          if (sortField === 'uploaded') {
                            const da = a.uploaded_at ? new Date(a.uploaded_at).getTime() : 0;
                            const db = b.uploaded_at ? new Date(b.uploaded_at).getTime() : 0;
                            return sortDir === 'asc' ? da - db : db - da;
                          }
                          const cmp = compareDisplayName(a.original_file_name, b.original_file_name);
                          return sortDir === 'asc' ? cmp : -cmp;
                        })
                        .map((file) => (
                        <tr key={file.id}>
                          <td className="files-table-col-select">
                            <input
                              type="checkbox"
                              checked={selectedFileIds.includes(file.id)}
                              onChange={() => toggleFileSelection(file.id)}
                              aria-label={`Select ${file.original_file_name}`}
                            />
                          </td>
                          <td className="file-name-cell">
                            <div className="file-name-row">
                              <button
                                type="button"
                                className="file-name-open-link"
                                onClick={() => handleOpenFile(file)}
                                title={
                                  isPdfFile(file)
                                    ? file.file_size_bytes != null
                                      ? `Open PDF in new tab — ${formatFileSize(file.file_size_bytes)}`
                                      : 'Open PDF in new tab'
                                    : file.file_size_bytes != null
                                      ? `Download — ${formatFileSize(file.file_size_bytes)}`
                                      : 'Download file'
                                }
                              >
                                {file.original_file_name}
                              </button>
                              <span className="file-version-muted" title="Current version">
                                v{file.current_version_number != null ? file.current_version_number : 1}
                              </span>
                            </div>
                          </td>
                          <td className="files-table-col-uploaded">{formatDisplayDate(file.uploaded_at)}</td>
                          <td className="file-actions">
                            <div className="file-actions-menu-root">
                              <button
                                type="button"
                                className="file-actions-menu-trigger"
                                aria-label={`More actions for ${file.original_file_name}`}
                                aria-haspopup="menu"
                                aria-expanded={fileMenu?.file?.id === file.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Read geometry here — not inside setState's updater. In Strict Mode
                                  // React may invoke the updater twice; the synthetic event can be
                                  // cleared so e.currentTarget is null on the second call.
                                  const el = e.currentTarget;
                                  if (!el) return;
                                  const rect = el.getBoundingClientRect();
                                  setFileMenu((prev) =>
                                    prev?.file?.id === file.id ? null : { file, rect }
                                  );
                                }}
                              >
                                <span aria-hidden="true" className="file-actions-menu-trigger-dots">
                                  ⋯
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <div className="no-folder-selected">
              Select a folder from the left to view files
            </div>
          )}
        </div>
      </div>

      {/* Upload dialog */}
      {showUploadDialog && (
        <UploadDialog
          file={pendingFile}
          folderOptions={insertFolderRows}
          initialTargetFolderKey={selectedFolder}
          getSuggestionsForFolder={getSuggestionsForFolder}
          onConfirm={handleUploadConfirm}
          onCancel={handleUploadCancel}
        />
      )}
      
      {/* Rename dialog */}
      {showRenameDialog && (
        <RenameDialog
          file={renameFile}
          suggestions={folderSuggestions}
          onConfirm={handleRenameConfirm}
          onCancel={() => {
            setShowRenameDialog(false);
            setRenameFile(null);
          }}
        />
      )}

      {showMoveDialog && moveFile && (
        <MoveFileDialog
          file={moveFile}
          fromLabel={resolveFolderLabelForFile(moveFile.folder_key, insertFolderRows)}
          destinationOptions={moveDestinationOptions}
          onConfirm={handleMoveConfirm}
          onCancel={handleMoveCancel}
          submitting={moveSubmitting}
        />
      )}

      {showBulkMoveDialog && bulkMoveFiles?.length > 0 && (
        <BulkMoveFilesDialog
          files={bulkMoveFiles}
          fromLabel={resolveFolderLabelForFile(bulkMoveFiles[0].folder_key, insertFolderRows)}
          destinationOptions={bulkMoveDestinationOptions}
          onConfirm={handleBulkMoveConfirm}
          onCancel={handleBulkMoveCancel}
          submitting={bulkMoveSubmitting}
        />
      )}

      {showBulkDeleteDialog && bulkDeleteFiles?.length > 0 && (
        <BulkDeleteConfirmDialog
          files={bulkDeleteFiles}
          onConfirm={handleBulkDeleteConfirm}
          onCancel={handleBulkDeleteCancel}
          submitting={bulkDeleteSubmitting}
          error={bulkDeleteError}
        />
      )}

      {/* Bulk upload modal – portaled to body so it appears above the left nav */}
      {showBulkUploadModal && bulkFiles && bulkFiles.length > 0 && createPortal(
        <BulkUploadModal
          files={bulkFiles}
          assignments={bulkAssignments}
          folderOptions={flattenFoldersForDropdown(template?.template_structure?.folders || [])}
          getSuggestionsForFolder={getSuggestionsForFolder}
          onAssignmentChange={handleBulkAssignmentChange}
          onApplyFolderToAll={handleApplyFolderToAll}
          onUploadAll={handleBulkUploadAll}
          onCancel={handleBulkUploadCancel}
          uploading={uploading}
          uploadStatus={bulkUploadStatus}
        />,
        document.body
      )}
      {showInsertDialog && insertTargetFile && (
        <InsertPagesDialog
          targetFile={insertTargetFile}
          allFiles={allFiles || folderFiles}
          selectedFolder={selectedFolder}
          folderRows={insertFolderRows}
          onConfirm={handleInsertConfirm}
          onPreviewTarget={() => handlePreviewPdfFile(insertTargetFile)}
          onCancel={() => {
            if (insertSubmitting) return;
            setShowInsertDialog(false);
            setInsertTargetFile(null);
          }}
          submitting={insertSubmitting}
        />
      )}
      {showVersionDialog && versionTargetFile && (
        <VersionHistoryDialog
          file={versionTargetFile}
          versions={versionList}
          onDownloadVersion={handleDownloadVersion}
          onClose={() => {
            setShowVersionDialog(false);
            setVersionTargetFile(null);
            setVersionList([]);
          }}
          loading={versionLoading}
        />
      )}

      {fileMenu && (
        <ClientFolderFileActionsMenuPortal
          fileMenu={fileMenu}
          onClose={() => setFileMenu(null)}
          canUpload={canUpload}
          canDelete={canDelete}
          onPreviewPdf={handlePreviewPdfFile}
          onDownload={handleDownload}
          onRename={handleRename}
          onMove={handleOpenMove}
          onVersions={handleOpenVersions}
          onInsert={handleOpenInsertDialog}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

export default ClientDocumentFolder;
